import React, { useEffect } from 'react'
import { addOrbitRigToLiveGraph } from '../lib/cameraRigInsert'
import { useCameraRigState } from '../hooks/useCameraRigState'

/**
 * CameraRigNotice — small viewport chip shown while the blueprint contains no
 * camera rig (Wave 2B: the app is always live, so the chip has no mode gate —
 * a rig-less blueprint always means the editor-camera fallback is driving).
 *
 * History: this used to be a large blocking banner, because a rig-less
 * blueprint meant an INERT camera and an unexplained frozen viewport is
 * indistinguishable from a broken build. Scene.jsx now falls back to the
 * editor OrbitControls in that state (the PlayCanvas/Unreal model — the editor
 * camera is the default, an authored rig takes over the moment camera nodes
 * exist), so the viewport is never frozen. What remains is the labeling duty:
 * the fallback must not be a SILENT substitution, or "why do my camera nodes
 * do nothing when I've only added half of them" becomes undiagnosable. Hence
 * a chip, not a banner — informative, with the one-click rig insert kept.
 *
 * window.__CAMERA_RIG_STATE__ stays published for tests
 * (event-graph-group.spec.js asserts 'ok'/'no-rig' transitions through it).
 */
export default function CameraRigNotice() {
  const state = useCameraRigState(true)

  useEffect(() => {
    if (typeof window !== 'undefined') window.__CAMERA_RIG_STATE__ = state
  }, [state])

  if (state === 'ok') return null

  const noGraph = state === 'no-graph'
  return (
    <div style={{
      // z 15 < LiveTunePanel's 20 — deliberately BELOW it.
      //
      // This used to be 30, to stop the expanded tune panel sitting on the chip
      // and eating the "+ Add rig" click in a narrow split viewport. The
      // reasoning was "the chip is pointerEvents:none except that button, so
      // outranking the panel costs nothing — panel clicks pass straight
      // through." That is wrong: the button below IS pointerEvents:'auto', so
      // wherever it lands on the panel it becomes an opaque hit target. Centred
      // at top:12 it landed square on the panel's collapse toggle, which sits in
      // the same 12–40px band at the panel's top-left corner — making the tune
      // panel impossible to expand/collapse, and hanging any Playwright click on
      // it forever (no default actionTimeout) until the test timed out.
      //
      // In a narrow viewport the two genuinely cannot both fit, so this is a
      // choice of who wins the hit test, and the panel must: it is a dense
      // interactive surface, whereas this chip is informational with one
      // convenience button that has an equivalent path (add the rig in the Event
      // Graph). The chip stays fully visible and clickable everywhere the panel
      // isn't, and the panel is dev-only (?tune=1) — production live mode has no
      // panel, so nothing outranks the chip there.
      position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
      zIndex: 15, pointerEvents: 'none',
      display: 'flex', alignItems: 'center', gap: 8,
      background: 'color-mix(in srgb, var(--m-panel) 92%, transparent)',
      border: '1px solid var(--m-line-hi)', borderRadius: 999,
      padding: '4px 6px 4px 12px',
      fontFamily: 'var(--m-font)', fontSize: 10, color: 'var(--m-fg-2)',
      whiteSpace: 'nowrap',
    }}>
      <span style={{ color: 'var(--m-warn)', letterSpacing: 0.5 }}>EDITOR CAMERA</span>
      <span>{noGraph ? 'no event graph loaded' : 'no camera rig in blueprint'}</span>
      {/* One-click upgrade: splice the Orbit rig into the live graph so the
          blueprint owns the camera. In-session — save in the editor to persist. */}
      {!noGraph && (
        <button
          type="button"
          onClick={() => addOrbitRigToLiveGraph()}
          data-testid="add-orbit-rig"
          title="Insert the Orbit Camera Rig into the Event Graph so the blueprint drives the camera"
          style={{
            pointerEvents: 'auto', cursor: 'pointer',
            background: 'var(--m-accent-soft)', border: '1px solid var(--m-accent)',
            borderRadius: 999, padding: '2px 10px',
            fontFamily: 'var(--m-font)', fontSize: 10, color: 'var(--m-fg)',
          }}
        >
          ＋ Add rig
        </button>
      )}
    </div>
  )
}
